import Cookies from 'js-cookie'
import type { AppSignInErrorDataModel } from '@/components/app-sign-in/types/app-sign-in-model'
import { useRememberLoginInfo } from '@/composables/use-remember-login-info'
import type { LoginManagerAccountType } from '@/enums/login-manager-account-type'
import { RouterName } from '@/enums/router-name'
import type { CorporateAccountUserProfile } from '@/models/api/corporate-account-user-profile'
import type { CorporateLoginData } from '@/models/api/corporate-login-data'
import type { ExceptionValidateDataType } from '@/models/api/exception-validate-data-type'
import type { LoginData } from '@/models/api/login-data'
import type { UserProfile } from '@/models/api/user-profile'
import router from '@/router'
import { api } from '@/services'
import { useIdentityStore } from '@/stores/use-identity-store'
import {
  useUserBindingStore,
  type UserBindingData
} from '@/stores/use-user-binding-store'
import { useUserStore } from '@/stores/use-user-store'
import { isLoading } from '@/utils/functions/is-loading'
import { CookiesKey } from '@library/enums/cookies-key'
import { useLocale } from '~library/composables'
import { Identity, RtnCode } from '~library/enums'
import { useCaptchaStore } from '~library/stores'
import { isApiSuccess } from '~library/utils/functions'

export interface UseInfiniteLoginType {
  loginData: LoginData
  isRememberLoginInfo: boolean
  accountType: LoginManagerAccountType
}

export interface UseCorporateLoginType {
  loginData: CorporateLoginData
  isRememberLoginInfo: boolean
  accountType: LoginManagerAccountType
}

export type LoginUserProfileData =
  | UserProfile
  | CorporateAccountUserProfile
  | null

export type UseInfiniteLoginSource =
  | RouterName.COMPANY_EMPLOYEE_BIND
  | RouterName.HOME

export function useLogin() {
  const loginErrorData = ref<AppSignInErrorDataModel | null>(null)

  const { resetCaptchaLoadedCount } = useCaptchaStore()
  const { setCorporateAccount, setUserProfile } = useUserStore()
  const { setUserBindingData } = useUserBindingStore()
  const { setIdentity } = useIdentityStore()
  const { setLocaleByApiLang } = useLocale()
  const { saveIsRememberLoginInfo } = useRememberLoginInfo()

  async function setLangByUserSetting() {
    const responseData = (await api.myAccount.getAccountInformation()).data

    if (isApiSuccess(responseData) && responseData.data.lang) {
      setLocaleByApiLang(responseData.data.lang)
    }
  }

  async function setCorporateLangByUserSetting(account: string) {
    const responseData = (await api.corporateAccount.getData(account)).data
    if (isApiSuccess(responseData) && responseData.data.lang) {
      setLocaleByApiLang(responseData.data.lang)
    }
  }

  async function infiniteLogin(params: {
    useLoginParams: UseInfiniteLoginType
    getCaptchaData: () => void
    setExceptionValidateData: (data: ExceptionValidateDataType) => void
    loginType: UseInfiniteLoginSource
  }) {
    const {
      getCaptchaData,
      setExceptionValidateData,
      useLoginParams,
      loginType
    } = params

    const responseData = await isLoading(
      api.login.login({
        ...useLoginParams.loginData,
        deviceId: navigator.userAgent,
        deviceToken: Cookies.get(CookiesKey.DEVICE_TOKEN) ?? ''
      })
    )

    if (isApiSuccess(responseData)) {
      setRememberLoginInfo({
        isRememberLoginInfo: useLoginParams.isRememberLoginInfo,
        identity: useLoginParams.loginData.identity,
        managerAccountType: useLoginParams.accountType,
        account: useLoginParams.loginData.account
      })
      if (
        loginType === RouterName.COMPANY_EMPLOYEE_BIND ||
        responseData.data?.binding
      ) {
        return responseData
      } else {
        resetCaptchaLoadedCount()
        router.push({ name: RouterName.JOIN_US })
      }
    }

    // 驗證碼錯誤
    if (
      responseData.rtnCode === RtnCode.VERIFICATION_CODE_ERROR ||
      responseData.rtnCode === RtnCode.VERIFICATION_CODE_EXPIRED
    ) {
      getCaptchaData()
      return
    }

    if (
      responseData.rtnCode === RtnCode.LATCHKEY_ERROR ||
      responseData.rtnCode === RtnCode.LOGIN_TRY_LATCHKEY_OVER_TIMES
    ) {
      loginErrorData.value = {
        data: responseData.data,
        rtnCode: responseData.rtnCode
      }
    }

    if (responseData.rtnCode === RtnCode.ACCOUNT_LATCHKEY_FAILURE) {
      setExceptionValidateData({
        account: useLoginParams.loginData.account,
        latchkey: useLoginParams.loginData.latchkey,
        message: responseData.message,
        rtnCode: responseData.rtnCode
      })
    }
  }

  async function corporateLogin(params: {
    useLoginParams: UseCorporateLoginType
    showModifyLatchKeyModal: () => void
  }) {
    const { showModifyLatchKeyModal, useLoginParams } = params

    const responseData = await isLoading(
      api.login.corporateLogin({
        ...useLoginParams.loginData
      })
    )

    if (isApiSuccess(responseData)) {
      setRememberLoginInfo({
        isRememberLoginInfo: useLoginParams.isRememberLoginInfo,
        identity: Identity.MAIN_MANAGER,
        managerAccountType: useLoginParams.accountType,
        account: useLoginParams.loginData.account
      })

      return responseData
    }

    if (responseData.rtnCode === RtnCode.FIRST_LOGIN_NEED_RESET_LATCHKEY) {
      showModifyLatchKeyModal()
      return
    }

    if (
      responseData.rtnCode === RtnCode.LATCHKEY_ERROR ||
      responseData.rtnCode === RtnCode.LOGIN_TRY_LATCHKEY_OVER_TIMES
    ) {
      loginErrorData.value = {
        data: responseData.data,
        rtnCode: responseData.rtnCode
      }
    }
  }

  async function setBindingDataAfterLogin(data: UserBindingData) {
    setUserBindingData(data)
  }

  async function setDataAfterLogin(params: {
    account: string
    identity: Identity
    userProfile: LoginUserProfileData
    setLang: boolean
  }) {
    const { account, identity, userProfile, setLang } = params
    // 刪除長榮的cookies
    Cookies.remove(CookiesKey.S_MEMBER)

    // 設定 Cookies DO_NOT_LOGOUT 變數
    Cookies.set(CookiesKey.DO_NOT_LOGOUT, 'true')

    resetCaptchaLoadedCount()
    setUserProfile(userProfile)
    setCorporateAccount(account)
    setIdentity(identity)

    if (
      setLang &&
      userProfile &&
      (userProfile.identity === Identity.CORPORATE_ACCOUNT ||
        userProfile.identity === Identity.CORPORATE_SUB_ACCOUNT)
    ) {
      await setCorporateLangByUserSetting(account)
    } else {
      await setLangByUserSetting()
    }
  }

  function setRememberLoginInfo(params: {
    isRememberLoginInfo: boolean
    identity: Identity
    managerAccountType: LoginManagerAccountType
    account: string
  }) {
    const { isRememberLoginInfo, identity, managerAccountType, account } =
      params

    if (isRememberLoginInfo) {
      saveIsRememberLoginInfo({
        identity: identity,
        managerAccountType: managerAccountType,
        account: account
      })
    } else {
      saveIsRememberLoginInfo(null)
    }
  }

  return {
    corporateLogin,
    infiniteLogin,
    setDataAfterLogin,
    loginErrorData,
    setBindingDataAfterLogin
  }
}
